import React from 'react';
import { ArrowRight, Check, ShoppingCart, Smartphone, Bluetooth, Battery } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTranslation } from '../context/TranslationContext';
import { useSanityProduct } from '../hooks/useSanityProducts';
import noteturnerText from '../data/noteturnerText';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../data/seoMeta';

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  var shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) return 'https://www.youtube.com/embed/' + shortsMatch[1] + '?autoplay=1&mute=1';
  var shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch) return 'https://www.youtube.com/embed/' + shortMatch[1] + '?autoplay=1&mute=1';
  var watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (watchMatch) return 'https://www.youtube.com/embed/' + watchMatch[1] + '?autoplay=1&mute=1';
  return url;
}

function getEmbedNoAutoplay(url) {
  if (!url) return null;
  var shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch) return 'https://www.youtube.com/embed/' + shortMatch[1];
  var shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) return 'https://www.youtube.com/embed/' + shortsMatch[1];
  var watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (watchMatch) return 'https://www.youtube.com/embed/' + watchMatch[1];
  return url;
}

function NoteTurnerPage() {
  var ref = useTranslation();
  var language = ref.language;
  var formatPrice = ref.formatPrice;
  var t = ref.t;
  var tx = noteturnerText[language] || noteturnerText.en;
  var sanityRef = useSanityProduct('noteturner');
  var sanityProduct = sanityRef.product;
  var navigate = useNavigate();

  var price = (sanityProduct && sanityProduct.price) || 34.99;
  var originalPrice = (sanityProduct && sanityProduct.originalPrice) || 39.99;

  var features = [tx.f1, tx.f2, tx.f3, tx.f4, tx.f5, tx.f6, tx.f7, tx.f8, tx.f9];

  var images = sanityProduct && sanityProduct.images && sanityProduct.images.length > 0
    ? sanityProduct.images
    : [
        'https://static.wixstatic.com/media/4a2ebb_895ee36e15ba4f70aee1a0473dc735c4~mv2.jpg',
        'https://static.wixstatic.com/media/4a2ebb_1edcb446ec0149f6838219bbd82383ae~mv2.jpg',
        'https://static.wixstatic.com/media/4a2ebb_ea2f1768ee41411f8963fa219ac26397~mv2.jpg'
      ];

  var videoUrl = null;
  if (sanityProduct && sanityProduct.videosByLanguage) {
    videoUrl = sanityProduct.videosByLanguage[language] || sanityProduct.videosByLanguage['en'];
  }
  var embedUrl = getYouTubeEmbedUrl(videoUrl);
  var batteryVideoEmbed = getEmbedNoAutoplay('https://youtu.be/xqaLjkKhx8c');
  var legsVideoEmbed = getEmbedNoAutoplay('https://youtube.com/shorts/KAGcRSbpRNg');
  var mobileSheetsVideoEmbed = getEmbedNoAutoplay('https://youtube.com/shorts/5psfuGHaUqs');
  // 4th how-to video — added Feb 2026 per Isaías.
  var quickStartVideoEmbed = getEmbedNoAutoplay('https://youtube.com/shorts/Uu3mNBA9VQc');

  function handleBuyNow() { navigate('/product/noteturner'); }

  var featureEls = [];
  for (var i = 0; i < features.length; i++) {
    featureEls.push(
      React.createElement('div', { key: i, className: 'flex items-center gap-3 p-4 rounded-xl bg-black border border-white/5' },
        React.createElement(Check, { className: 'w-5 h-5 text-[#d4af37] flex-shrink-0' }),
        React.createElement('span', { className: 'text-neutral-300 text-sm' }, features[i])
      )
    );
  }

  var galleryEls = [];
  var showImages = images.slice(0, 6);
  for (var j = 0; j < showImages.length; j++) {
    galleryEls.push(
      React.createElement('img', { key: j, src: showImages[j], alt: 'NoteTurner ' + (j + 1), className: 'rounded-2xl w-full aspect-square object-cover' })
    );
  }

  var steps = [
    [tx.s1t, tx.s1d], [tx.s2t, tx.s2d], [tx.s3t, tx.s3d],
    [tx.s4t, tx.s4d], [tx.s5t, tx.s5d], [tx.s6t, tx.s6d]
  ];
  var stepEls = [];
  for (var s = 0; s < steps.length; s++) {
    stepEls.push(
      React.createElement('div', { key: s, className: 'flex gap-4' },
        React.createElement('div', { className: 'w-10 h-10 rounded-full bg-[#d4af37] flex items-center justify-center flex-shrink-0 text-black font-bold' }, String(s + 1)),
        React.createElement('div', null,
          React.createElement('h4', { className: 'text-white font-semibold mb-1' }, steps[s][0]),
          React.createElement('p', { className: 'text-neutral-400 text-sm' }, steps[s][1])
        )
      )
    );
  }

  return React.createElement(Layout, null,
    React.createElement(SEO, { title: PAGE_SEO['noteturner'].title, description: PAGE_SEO['noteturner'].description, url: '/noteturner' }),
    // Hero
    React.createElement('section', { className: 'relative min-h-screen flex items-center pt-20' },
      React.createElement('div', { className: 'absolute inset-0 bg-gradient-to-br from-neutral-900 via-black to-black' }),
      React.createElement('div', { className: 'relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24' },
        React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-2 gap-16 items-center' },
          React.createElement('div', null,
            React.createElement('span', { className: 'inline-block px-4 py-2 rounded-full bg-[#d4af37]/10 text-[#d4af37] text-sm font-semibold mb-6' }, t('noteTurnerBestseller')),
            React.createElement('h1', { className: 'text-5xl md:text-7xl font-bold text-white mb-4' }, 'NoteTurner'),
            React.createElement('p', { className: 'text-xl text-neutral-300 mb-4' }, tx.tagline),
            React.createElement('p', { className: 'text-neutral-500 mb-4 leading-relaxed' }, tx.desc),
            React.createElement('p', { className: 'text-neutral-500 mb-4 text-sm leading-relaxed' }, tx.instruments),
            React.createElement('p', { className: 'text-neutral-500 mb-8 text-sm leading-relaxed' }, tx.controls),
            React.createElement('div', { className: 'flex items-baseline gap-4 mb-10' },
              React.createElement('span', { className: 'text-5xl font-bold text-[#d4af37]' }, formatPrice(price)),
              React.createElement('span', { className: 'text-2xl text-neutral-600 line-through' }, formatPrice(originalPrice)),
              React.createElement('span', { className: 'px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium' }, t('save') + ' ' + formatPrice(originalPrice - price))
            ),
            React.createElement('button', { onClick: handleBuyNow, 'data-testid': 'noteturner-buy-btn', className: 'inline-flex items-center gap-3 bg-[#d4af37] text-black hover:bg-white px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105' },
              React.createElement(ShoppingCart, { className: 'w-6 h-6' }), t('buyNow')
            )
          ),
          React.createElement('div', { className: 'relative space-y-6' },
            React.createElement('div', { className: 'absolute -inset-8 bg-[#d4af37]/10 rounded-3xl blur-3xl' }),
            embedUrl ? React.createElement('iframe', { src: embedUrl, title: 'NoteTurner Video', className: 'relative w-full aspect-video rounded-2xl bg-neutral-900 shadow-2xl', frameBorder: '0', allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture', allowFullScreen: true }) : null,
            React.createElement('img', { src: images[0], alt: 'NoteTurner', className: 'relative rounded-2xl w-full shadow-2xl' })
          )
        )
      )
    ),

    // Features
    React.createElement('section', { className: 'py-24 bg-neutral-950' },
      React.createElement('div', { className: 'max-w-5xl mx-auto px-6 lg:px-12' },
        React.createElement('div', { className: 'text-center mb-12' },
          React.createElement('p', { className: 'text-sm tracking-widest uppercase text-[#d4af37] mb-4' }, t('features')),
          React.createElement('h2', { className: 'text-4xl font-bold text-white' }, t('whyYouLoveIt'))
        ),
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' }, featureEls),
        React.createElement('p', { className: 'text-center text-neutral-400 mt-10 text-lg' }, tx.closing),
        React.createElement('p', { className: 'text-center text-[#d4af37] mt-2 font-semibold text-lg' }, tx.cta)
      )
    ),

    // Sync Guide
    React.createElement('section', { className: 'py-24 bg-black' },
      React.createElement('div', { className: 'max-w-5xl mx-auto px-6 lg:px-12' },
        React.createElement('div', { className: 'text-center mb-16' },
          React.createElement('p', { className: 'text-sm tracking-widest uppercase text-[#d4af37] mb-4' }, tx.setupGuide),
          React.createElement('h2', { className: 'text-4xl md:text-5xl font-bold text-white' }, tx.syncTitle)
        ),
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-12 mb-16' },
          React.createElement('div', { className: 'space-y-6' }, stepEls),
          React.createElement('div', null,
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-8' },
              React.createElement('div', { className: 'flex items-center gap-3 mb-6' },
                React.createElement(Smartphone, { className: 'w-6 h-6 text-[#d4af37]' }),
                React.createElement('h3', { className: 'text-xl font-bold text-white' }, tx.compatibleApps)
              ),
              React.createElement('div', { className: 'mb-6' },
                React.createElement('p', { className: 'text-[#d4af37] text-xs uppercase tracking-widest mb-3 font-semibold' }, 'iOS (iPad / iPhone)'),
                React.createElement('p', { className: 'text-white' }, 'ForScore'),
                React.createElement('p', { className: 'text-white' }, 'Piascore'),
                React.createElement('p', { className: 'text-white' }, 'MobileSheets')
              ),
              React.createElement('div', { className: 'mb-6' },
                React.createElement('p', { className: 'text-[#d4af37] text-xs uppercase tracking-widest mb-3 font-semibold' }, 'Android'),
                React.createElement('p', { className: 'text-white' }, 'MobileSheets')
              ),
              React.createElement('div', { className: 'flex items-center gap-2 pt-4 border-t border-white/10' },
                React.createElement(Bluetooth, { className: 'w-4 h-4 text-[#d4af37]' }),
                React.createElement('p', { className: 'text-neutral-500 text-sm' }, tx.compatNote)
              )
            )
          )
        )
      )
    ),

    // How-To Videos
    React.createElement('section', { className: 'py-24 bg-neutral-950' },
      React.createElement('div', { className: 'max-w-5xl mx-auto px-6 lg:px-12' },
        React.createElement('div', { className: 'text-center mb-12' },
          React.createElement('h2', { className: 'text-4xl font-bold text-white' }, tx.howToVideos)
        ),
        React.createElement('div', { className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8' },
          React.createElement('div', null,
            React.createElement('div', { className: 'flex items-center gap-2 mb-4' },
              React.createElement(Battery, { className: 'w-5 h-5 text-[#d4af37]' }),
              React.createElement('h3', { className: 'text-lg font-semibold text-white' }, tx.batteryTitle)
            ),
            React.createElement('iframe', { src: batteryVideoEmbed, title: 'Battery', className: 'w-full aspect-video rounded-2xl bg-neutral-900', frameBorder: '0', allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture', allowFullScreen: true }),
            React.createElement('p', { className: 'text-neutral-500 text-sm mt-3' }, tx.batteryNote)
          ),
          React.createElement('div', null,
            React.createElement('h3', { className: 'text-lg font-semibold text-white mb-4' }, tx.legsTitle),
            React.createElement('iframe', { src: legsVideoEmbed, title: 'Legs', className: 'w-full aspect-video rounded-2xl bg-neutral-900', frameBorder: '0', allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture', allowFullScreen: true })
          ),
          React.createElement('div', null,
            React.createElement('div', { className: 'flex items-center gap-2 mb-4' },
              React.createElement(Smartphone, { className: 'w-5 h-5 text-[#d4af37]' }),
              React.createElement('h3', { className: 'text-lg font-semibold text-white' }, tx.mobileSheetsTitle)
            ),
            React.createElement('iframe', { src: mobileSheetsVideoEmbed, title: 'MobileSheets', className: 'w-full aspect-video rounded-2xl bg-neutral-900', frameBorder: '0', allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture', allowFullScreen: true }),
            React.createElement('p', { className: 'text-neutral-500 text-sm mt-3' }, tx.mobileSheetsNote)
          ),
          // 4th video — Quick Start / Easiest way to turn pages
          React.createElement('div', null,
            React.createElement('h3', { className: 'text-lg font-semibold text-white mb-4' }, tx.quickStartTitle || 'Quick Start'),
            React.createElement('iframe', { src: quickStartVideoEmbed, title: 'Quick Start', className: 'w-full aspect-video rounded-2xl bg-neutral-900', frameBorder: '0', allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture', allowFullScreen: true })
          )
        )
      )
    ),

    // Gallery
    React.createElement('section', { className: 'py-24 bg-black' },
      React.createElement('div', { className: 'max-w-7xl mx-auto px-6 lg:px-12' },
        React.createElement('div', { className: 'text-center mb-16' },
          React.createElement('h2', { className: 'text-4xl font-bold text-white' }, tx.seeItInAction)
        ),
        React.createElement('div', { className: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6' }, galleryEls)
      )
    ),

    // CTA
    React.createElement('section', { className: 'py-24 bg-neutral-950' },
      React.createElement('div', { className: 'max-w-4xl mx-auto px-6 lg:px-12 text-center' },
        React.createElement('h2', { className: 'text-4xl font-bold text-white mb-6' }, t('noteTurnerCta')),
        React.createElement('button', { onClick: handleBuyNow, className: 'inline-flex items-center gap-2 bg-[#d4af37] text-black hover:bg-white px-8 py-4 rounded-full font-semibold transition-all' },
          t('getNoteTurner') + ' - ' + formatPrice(price),
          React.createElement(ArrowRight, { className: 'w-5 h-5' })
        )
      )
    )
  );
}

export default NoteTurnerPage;
